import {
	createContext,
	ReactNode,
	useCallback,
	useContext,
	useEffect,
	useState,
} from 'react';

import { ICart } from '@quizzach/interfaces';
import { consoleDev, ConsoleStatus, fetchData } from '@quizzach/utils';
import { useAuth } from './useAuthContext';

interface ICartCountContext {
	cartCount: number;
	refreshCartCount: () => Promise<void>;
}

const CartContext = createContext<ICartCountContext>({
	cartCount: 0,
	refreshCartCount: async () => undefined,
});

export const useCartContext = () => useContext(CartContext);

// Lightweight shared cart size for the nav badge. The cart page keeps its own
// full useCart state; mutations elsewhere call refreshCartCount to stay in sync.
export const CartProvider = ({ children }: { children: ReactNode }) => {
	const { isAuthenticated } = useAuth();
	const [cartCount, setCartCount] = useState(0);

	const refreshCartCount = useCallback(async () => {
		if (!window.localStorage.getItem('userToken')) {
			setCartCount(0);
			return;
		}
		try {
			const response = await fetchData<ICart>(
				'/api/cart',
				{ method: 'GET' },
				{ authRoute: true }
			);
			if (response.status < 300 && response.data) {
				setCartCount(response.data.cart_items?.length ?? 0);
			}
		} catch (err) {
			consoleDev('refreshCartCount', ConsoleStatus.NET, err);
		}
	}, []);

	useEffect(() => {
		if (isAuthenticated) {
			refreshCartCount();
		} else if (isAuthenticated === false) {
			setCartCount(0);
		}
	}, [isAuthenticated, refreshCartCount]);

	return (
		<CartContext.Provider value={{ cartCount, refreshCartCount }}>
			{children}
		</CartContext.Provider>
	);
};
